/**
 * Personal Oblíbené sales-point IDs for Browse ★ filter (Stage 4).
 * Guest: pendingFavoriteSpContacts localStorage. Signed-in: L3 contacts query.
 * Not aggregate popularity — never uses favoriteCount / favoriteCountDesc.
 * G8 — also exposes raw favorite contacts for ☆ universe list (not discovery intersection).
 */

import { useMemo, useSyncExternalStore } from 'react';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  getPendingFavoriteSpContactsSnapshot,
  getPendingFavoriteSpContactsServerSnapshot,
  readPendingFavoriteSpContacts,
  subscribePendingFavoriteSpContacts,
  type PendingFavoriteSpContact,
} from '../account/pendingFavoriteSpContacts.js';
import { useSalesPointContactsByToken } from '../account/useSalesPointContactsByToken.js';
import type { SalesPointContactItemDTO } from '../account/salesPointContactsTypes.js';

export type PersonalFavoriteSalesPointIdsState = {
  readonly favoriteSalesPointIds: ReadonlySet<number>;
  readonly isLoading: boolean;
  /** Signed-in L3 contact rows (favorites when isFavorite !== false). */
  readonly signedInFavoriteContacts: readonly SalesPointContactItemDTO[];
  /** Guest pending favorite contacts (☆ universe). */
  readonly guestPendingFavorites: readonly PendingFavoriteSpContact[];
  readonly signedIn: boolean;
};

function guestFavoriteSalesPointIdsFromSnapshot(snapshot: string): ReadonlySet<number> {
  if (snapshot.length === 0) {
    return new Set();
  }
  const contacts = readPendingFavoriteSpContacts();
  const ids = new Set<number>();
  for (const contact of contacts) {
    if (typeof contact.salesPointId === 'number' && contact.salesPointId > 0) {
      ids.add(contact.salesPointId);
    }
  }
  return ids;
}

export function usePersonalFavoriteSalesPointIds(): PersonalFavoriteSalesPointIdsState {
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });
  const favoritesQuery = useSalesPointContactsByToken(signedIn);

  const guestSnapshot = useSyncExternalStore(
    subscribePendingFavoriteSpContacts,
    getPendingFavoriteSpContactsSnapshot,
    getPendingFavoriteSpContactsServerSnapshot,
  );

  const guestIds = useMemo(
    () => guestFavoriteSalesPointIdsFromSnapshot(guestSnapshot),
    [guestSnapshot],
  );

  const signedInIds = useMemo(() => {
    if (!signedIn) {
      return new Set<number>();
    }
    return new Set(
      (favoritesQuery.data?.items ?? [])
        .filter((item) => item.isFavorite !== false)
        .map((item) => item.salesPointId)
        .filter((id): id is number => typeof id === 'number' && id > 0),
    );
  }, [favoritesQuery.data?.items, signedIn]);

  const favoriteSalesPointIds = signedIn ? signedInIds : guestIds;
  const isLoading = signedIn && favoritesQuery.isPending && favoritesQuery.fetchStatus !== 'idle';

  const signedInFavoriteContacts = useMemo((): readonly SalesPointContactItemDTO[] => {
    if (!signedIn) {
      return [];
    }
    return favoritesQuery.data?.items ?? [];
  }, [favoritesQuery.data?.items, signedIn]);

  const guestPendingFavorites = useMemo((): readonly PendingFavoriteSpContact[] => {
    if (signedIn || guestSnapshot.length === 0) {
      return [];
    }
    return readPendingFavoriteSpContacts();
  }, [guestSnapshot, signedIn]);

  return {
    favoriteSalesPointIds,
    isLoading,
    signedInFavoriteContacts,
    guestPendingFavorites,
    signedIn,
  };
}
